import { FC, Fragment, useReducer } from 'react';

import { useFormik } from 'formik';
import { Plus } from 'lucide-react';
import { omit } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { DistributionLinkForm, initialValues } from './form';

import { distributionLinksService } from 'v2/api/distributionLinksService';
import { rewardsService } from 'v2/api/rewardsService';
import { RewardsFormControl } from 'v2/components/form/rewardsFormControl/RewardsFormControl';
import { handleHttpError } from 'v2/utils';

interface CreateDistributionLinkButtonProps {
  onSuccess: VoidFunction;
}

export const CreateDistributionLinkButton: FC<CreateDistributionLinkButtonProps> = ({ onSuccess }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const formik = useFormik<DistributionLinkForm>({
    initialValues,
    onSubmit: (values) => {
      triggerRewards(values.rewards).then((rewardIds) => {
        triggerCreate({ ...omit(values, ['rewards']), rewardIds });
      });
    },
  });

  const { trigger: triggerCreate, isMutating } = useSWRMutation(
    'createDistributionLink',
    distributionLinksService.createDistributionLink,
    {
      onSuccess: () => {
        onSuccess();
        toast.success('Distribution link has been successfully created');
        handleClose();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerRewards, isMutating: isMutatingRewards } = useSWRMutation(
    'createRewards',
    rewardsService.createRewards,
    { onError: handleHttpError }
  );

  const handleClose = () => {
    setOpen();
    formik.resetForm();
  };

  return (
    <Fragment>
      <Button onClick={setOpen} startIcon={<Plus />} variant="contained" size="large">
        Create Distribution Link
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
        <DialogTitle>Create Distribution Link</DialogTitle>

        <DialogContent>
          <form id="distributionLinkForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
            <TextField
              name="key"
              value={formik.values.key}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Key"
              required
            />

            <TextField
              name="maxUses"
              value={formik.values.maxUses}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Max Uses"
              type="number"
              required
            />

            <RewardsFormControl formik={formik} required />
          </form>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button
            form="distributionLinkForm"
            type="submit"
            variant="contained"
            loading={isMutating || isMutatingRewards}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};
